import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function FonasaNoVendido() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cantidad, setCantidad] = useState('');
  const [fecha, setFecha] = useState(null);
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear] = useState(new Date().getFullYear());

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try { const data = await api.getFonasaNoVendido(selectedMonth, selectedYear); setRecords(data); }
    catch (error) { /* handled */ } finally { setLoading(false); }
  }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(cantidad);
    if (!qty || qty <= 0) { toast.error('Ingresa una cantidad valida'); return; }
    setSaving(true);
    try {
      const payload = { cantidad: qty, observaciones };
      if (fecha) payload.fecha = format(fecha, 'yyyy-MM-dd');
      await api.addFonasaNoVendido(payload);
      toast.success(`${qty} Fonasa agregados`); setCantidad(''); setFecha(null); setObservaciones(''); fetchRecords();
    } catch (error) { toast.error('Error al registrar'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este registro?')) return;
    try { await api.deleteFonasaNoVendido(id); toast.success('Registro eliminado'); fetchRecords(); }
    catch (error) { toast.error('Error al eliminar'); }
  };

  const totalMes = records.reduce((sum, r) => sum + r.cantidad, 0);

  return (
    <div className="space-y-6" data-testid="fonasa-listado-more">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Fonasa por Listado More</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Registros Fonasa por listado More</p>
        </div>
        <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="form-input w-auto" data-testid="month-selector">
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      </div>

      <form onSubmit={handleSubmit} className="mac-card p-5">
        <h3 className="text-[13px] font-semibold text-[#0A0A0A] mb-4">Agregar Fonasa</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="form-label">Cantidad *</label>
            <input type="number" min="1" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className="form-input" placeholder="Ej: 10" data-testid="fonasa-cantidad" />
          </div>
          <div>
            <label className="form-label">Fecha</label>
            <Popover><PopoverTrigger asChild><button type="button" className="form-input flex items-center justify-between text-left" data-testid="fonasa-fecha">{fecha ? format(fecha, 'dd/MM/yyyy') : <span className="text-gray-400">Hoy</span>}<CalendarDays size={16} className="text-gray-400" /></button></PopoverTrigger>
              <PopoverContent className="w-auto p-0 mac-popover" align="start"><Calendar mode="single" selected={fecha} onSelect={setFecha} locale={es} initialFocus className="rounded-md" disabled={(date) => date > new Date()} /></PopoverContent></Popover>
          </div>
          <div>
            <label className="form-label">Observaciones</label>
            <input type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className="form-input" placeholder="Opcional" data-testid="fonasa-obs" />
          </div>
          <button type="submit" disabled={saving} className="btn-primary flex items-center justify-center gap-2" data-testid="fonasa-submit"><Plus size={16} />{saving ? 'Guardando...' : 'Agregar'}</button>
        </div>
      </form>

      <div className="mac-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="metric-label">Total Fonasa por Listado More - {MONTHS[selectedMonth - 1]} {selectedYear}</div>
            <div className="text-3xl font-bold text-[#0082D2]">{totalMes.toLocaleString('es-UY')}</div>
          </div>
          <div className="text-right text-[12px] text-gray-500">{records.length} registro(s)</div>
        </div>
      </div>

      <div className="mac-card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200"><h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Historial - {MONTHS[selectedMonth - 1]}</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className="table-header">Fecha</th><th className="table-header">Cantidad</th><th className="table-header">Observaciones</th><th className="table-header w-16"></th></tr></thead>
            <tbody>
              {loading ? (<tr><td colSpan="4" className="table-cell text-center text-gray-500">Cargando...</td></tr>
              ) : records.length === 0 ? (<tr><td colSpan="4" className="table-cell text-center text-gray-500">Sin registros en {MONTHS[selectedMonth - 1]}</td></tr>
              ) : (records.map(r => (
                <tr key={r.id} className="table-row">
                  <td className="table-cell text-[12px]">{new Date(r.fecha).toLocaleDateString('es-UY')}</td>
                  <td className="table-cell"><span className="inline-flex px-2.5 py-0.5 rounded-full bg-[#0082D2]/10 text-[#0082D2] text-[12px] font-bold">{r.cantidad}</span></td>
                  <td className="table-cell text-[12px] text-gray-500">{r.observaciones || '-'}</td>
                  <td className="table-cell"><button onClick={() => handleDelete(r.id)} className="btn-ghost p-1.5 text-[#EF4444]" data-testid={`delete-${r.id}`}><Trash2 size={14} /></button></td>
                </tr>
              )))}
              {records.length > 0 && (
                <tr className="border-t-2 border-[#00205B]/20">
                  <td className="table-cell font-bold text-[#0A0A0A]">TOTAL</td>
                  <td className="table-cell"><span className="inline-flex px-3 py-1 rounded-full bg-[#00205B]/10 text-[#00205B] text-[13px] font-bold">{totalMes.toLocaleString('es-UY')}</span></td>
                  <td colSpan="2" className="table-cell"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
